import React, { useState } from 'react';
import { useNotificationList } from '@/hooks/queries/notification/useList';
import { useNotificationCreate, useNotificationUpdate, useNotificationDelete } from '@/hooks/mutations/notification';
import { Notification, NotificationInput } from '@/types/notifications';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/fields';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, Bell } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { FileUploadInput } from '@/components/FileUploadInput';
import { CourseSelect } from '@/components/CourseSelect';
import { CourseCell } from '@/components/CourseCell';
import { getMediaUrl } from '@/utils/common';
import { Pagination } from '@/components/Pagination';

const NotificationsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  const [formData, setFormData] = useState<NotificationInput>({
    photo: '',
    targetId: '',
    translations: [
      { locale: 'uz', title: '', description: '', body: '' },
      { locale: 'ru', title: '', description: '', body: '' },
      { locale: 'en', title: '', description: '', body: '' },
    ],
  });

  const { data: notifsData, isLoading, refetch } = useNotificationList({ pageNumber: page, pageSize: 20 });
  const { mutate: createNotification, isPending: isCreating } = useNotificationCreate();
  const { mutate: updateNotification, isPending: isUpdating } = useNotificationUpdate();
  const { mutate: deleteNotification } = useNotificationDelete();

  const handleOpenCreate = () => {
    setSelectedNotification(null);
    setFormData({
      photo: '',
      targetId: '',
      translations: [
        { locale: 'uz', title: '', description: '', body: '' },
        { locale: 'ru', title: '', description: '', body: '' },
        { locale: 'en', title: '', description: '', body: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (notif: Notification) => {
    setSelectedNotification(notif);
    setFormData({
      photo: notif.photo || '',
      targetId: notif.targetId || '',
      translations: [
        { locale: 'uz', title: notif.title || '', description: notif.description || '', body: notif.body || '' },
        { locale: 'ru', title: '', description: '', body: '' },
        { locale: 'en', title: '', description: '', body: '' },
      ],
    });
    setIsModalOpen(true);
  };

  const handleTranslationChange = (locale: 'uz' | 'ru' | 'en', field: 'title' | 'description' | 'body', value: string) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((t) => (t.locale === locale ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      targetId: formData.targetId ? formData.targetId : undefined,
    };

    if (selectedNotification) {
      updateNotification(
        { id: selectedNotification.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Bildirishnoma yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Bildirishnoma yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createNotification(payload, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi bildirishnoma yuborildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Bildirishnoma yuborilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedNotification) return;
    deleteNotification(selectedNotification.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Bildirishnoma o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedNotification(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Bildirishnoma o'chirilmadi", variant: 'destructive' }),
    });
  };

  const notificationsList: Notification[] = notifsData?.data?.data || notifsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Bildirishnomalar</h1>
          <p className="text-sm text-slate-500 mt-1">Platformadagi barcha foydalanuvchilarga yuboriladigan xabarlar va yangiliklar</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi bildirishnoma
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : notificationsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Bell className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha bildirishnomalar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Rasm</th>
                  <th className="px-4 py-3.5">Sarlavha</th>
                  <th className="px-4 py-3.5">Qisqa Tavsif</th>
                  <th className="px-4 py-3.5">Bog'langan Kurs</th>
                  <th className="px-4 py-3.5">Yaratilgan Sana</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {notificationsList.map((notif) => (
                  <tr key={notif.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3">
                      {notif.photo ? (
                        <img src={getMediaUrl(notif.photo)} alt={notif.title} className="size-10 rounded-lg object-cover border border-slate-200" />
                      ) : (
                        <div className="size-10 rounded-lg bg-blue-50 text-primary flex items-center justify-center font-bold">
                          <Bell className="size-5" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{notif.title || '—'}</td>
                    <td className="px-4 py-3 max-w-xs truncate text-slate-600">{notif.description || '—'}</td>
                    <td className="px-4 py-3">
                      <CourseCell courseId={notif.targetId} fallbackText="Umumiy bildirishnoma" />
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{notif.createdAt?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(notif)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedNotification(notif);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={notifsData?.meta?.pagination ?? notifsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedNotification ? 'Bildirishnomani tahrirlash' : 'Yangi bildirishnoma yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-4">
              <FileUploadInput
                label="Rasm (Ixtiyoriy)"
                value={formData.photo || ''}
                onChange={(url) => setFormData({ ...formData, photo: url })}
              />
              <CourseSelect
                label="Bog'langan Kurs (Ixtiyoriy)"
                value={formData.targetId || ''}
                onChange={(id) => setFormData({ ...formData, targetId: id })}
                allowEmpty={true}
                emptyLabel="Umumiy bildirishnoma (kursga bog'lanmagan)"
              />
            </div>

            <div className="pt-2">
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Ko'p tilli ma'lumotlar</Label>
              <Tabs defaultValue="uz" className="w-full">
                <TabsList className="grid grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger value="uz" className="rounded-lg text-xs font-medium">O'zbekcha (UZ)</TabsTrigger>
                  <TabsTrigger value="ru" className="rounded-lg text-xs font-medium">Русский (RU)</TabsTrigger>
                  <TabsTrigger value="en" className="rounded-lg text-xs font-medium">English (EN)</TabsTrigger>
                </TabsList>

                {(['uz', 'ru', 'en'] as const).map((locale) => {
                  const translation = formData.translations.find((t) => t.locale === locale) || {
                    title: '',
                    description: '',
                    body: '',
                  };
                  return (
                    <TabsContent key={locale} value={locale} className="space-y-3 pt-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Sarlavha ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.title || ''}
                          onChange={(e) => handleTranslationChange(locale, 'title', e.target.value)}
                          placeholder="Yangi kurs ochildi!"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Qisqa tavsif ({locale.toUpperCase()})</Label>
                        <Input
                          value={translation.description || ''}
                          onChange={(e) => handleTranslationChange(locale, 'description', e.target.value)}
                          placeholder="Backend Node.js kursi ro'yxatdan o'tishga tayyor"
                          className="rounded-xl"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-600">Batafsil matn ({locale.toUpperCase()})</Label>
                        <MarkdownEditor
                          value={translation.body || ''}
                          onChange={(v) => handleTranslationChange(locale, 'body', v)}
                          placeholder="Bildirishnoma to'liq matni..."
                          minHeight={100}
                        />
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Yuborish'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Bildirishnomani o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu bildirishnoma butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default NotificationsPage;
